<script setup lang="ts">
// Framing for ONE cart item, configured the way the artwork and set checkouts configure it —
// same options, same calculator, same live preview — but self-contained, because a cart holds a
// mix of artworks and sets and every one of them is asked separately.
//
// A set is handled exactly as the set page handles it: the specifications are configured once, on
// the first available framable piece (which is also the piece the preview draws), and are then
// priced against every other eligible piece at ITS OWN size. The charge is the sum.
import { computed, ref, watch, nextTick, onBeforeUnmount } from "vue";
import { useMediaQuery } from "@vueuse/core";
import MeasurementUnitToggle from "~/components/molecules/MeasurementUnitToggle.vue";
import { useMeasurementUnit } from "~/composables/useMeasurementUnit";
import Button from "~/components/atoms/Button.vue";
import FramingCalculator from "~/components/internal/FramingCalculator.vue";
import FrameVisualizer from "~/components/internal/FrameVisualizer.vue";
import { getArtworkImageSrc, getFramedArtworkImageSrc, formatFramingCost, parseFramingCost, totalFramingCost } from "~/utils";
import { parseArtworkDimensions } from "~/utils/shippingCalculator";
import {
  calculateFramingBreakdown,
  resolveFramingArtworkType,
  specsForArtworkType,
  stretcherBarDepthInches,
  type FramingMode,
} from "~/utils/framingCalculator";
import {
  buildPieceFraming,
  buildSetFramingRecord,
  noteToFramingReason,
  needsMountOrSideDepth,
  type PieceFraming,
  type SetFramingRecord,
} from "~/utils/framingOrderPayload";

const { globalUnit } = useMeasurementUnit();
const sizeUnit = ref<'in' | 'cm'>(globalUnit.value);

watch(globalUnit, (newUnit) => {
  sizeUnit.value = newUnit;
});

// What the checkout needs back from this item: the buyer's choice, the specs to store, what it
// costs, and — for shipping — which pieces the choice actually reaches and how deep their box is.
export interface CartFramingSelection {
  cartItemId: string;
  itemType: "artwork" | "set";
  itemName: string;
  choice: string;
  mode: FramingMode | null;
  specs: Record<string, any> | null;
  cost: number;
  frameDepthInches: number;
  framedArtworkIds: string[];
  perArtwork: Array<{
    id: string;
    name: string;
    size: string;
    artworkType: string | null;
    cost: number;
    note: string | null;
    components?: Record<string, any>;
  }>;
  // What the checkout draws in its own left column from lg up, where every configured item's
  // preview is listed together. Null while there is nothing to preview.
  preview: { label: string; props: Record<string, any> } | null;
  // What actually gets stored on the order for this item's framing — the same clean shape the
  // artwork and set checkouts store, built by ~/utils/framingOrderPayload. Null when there is
  // nothing to configure. This is additive alongside `specs`/`perArtwork` above, which stay
  // exactly as they were for the shipping/pricing logic that already depends on them.
  framingRecord: PieceFraming | SetFramingRecord | null;
  // False for an item with nothing to configure — a non-framable artwork, or a set whose available
  // pieces can none of them take a new frame. Those must not block checkout.
  requiresChoice: boolean;
  isComplete: boolean;
}

const props = defineProps<{
  item: any;
  // Set by the parent when the buyer tries to pay with this item unanswered.
  error?: string;
}>();

const emit = defineEmits<{
  (e: "update:selection", value: CartFramingSelection): void;
  // Lets the parent (CartSlideover) know this item's own framing dialog is open, so it can hold
  // off dismissing the checkout modal itself — the dialog is Teleported to <body>, outside the
  // checkout modal's own DOM subtree, so a click on its backdrop would otherwise read as "outside".
  (e: "update:dialogOpen", value: boolean): void;
}>();

const isSet = computed(() => props.item?.type === "set");

// A set ships only the pieces that are still available, so only those are framed and priced.
const pieces = computed<any[]>(() => {
  if (!isSet.value) return props.item ? [props.item] : [];
  return (props.item.setArtworks || []).filter(
    (a: any) => !["Sold", "Reserved", "Gift"].includes(a?.availability ?? "")
  );
});

const isNonFramable = (art: any) => (art?.framable ?? "").toLowerCase() === "non-framable";

// Non-framable is the only real exclusion, for a set or a single artwork alike. Already-framed
// never excludes a piece — it can still take a new frame (or be stretched), the same as a single
// artwork's own already-framed case: Framed means a new frame, Unframed means keep it as-is.
// Excluding it here used to disable "Framed" entirely for a set where every piece happened to
// already be framed, which is wrong — those pieces can genuinely be reframed.
const exclusionReason = (art: any): string | null => {
  if (isNonFramable(art)) return "Not framable";
  return null;
};

const eligiblePieces = computed(() => pieces.value.filter((a) => !exclusionReason(a)));

// The piece the specifications are configured on, and the one the preview draws.
const primaryPiece = computed(() => eligiblePieces.value[0] ?? null);

const primaryDimensions = computed(() => parseArtworkDimensions(primaryPiece.value));

const canFrame = computed(() => eligiblePieces.value.length > 0);

// Stretching is a canvas-only build. For a set the one choice reaches every eligible piece, so it
// is offered only when all of them are canvas.
const canStretch = computed(
  () =>
    canFrame.value &&
    eligiblePieces.value.every((a: any) => (a?.artwork_category ?? "").trim() === "Canvas")
);


// Nothing to ask when the one artwork cannot be framed, or when no piece of a set can be.
const requiresChoice = computed(() => canFrame.value);

const choice = ref("");
const specs = ref<Record<string, any> | null>(null);

const mode = computed<FramingMode | null>(() => {
  if (choice.value === "Framed") return "frame";
  if (choice.value === "Stretched") return "stretch";
  return null;
});

const framingNoun = computed(() => (mode.value === "stretch" ? "Stretching" : "Framing"));
const framingVerb = computed(() => (mode.value === "stretch" ? "stretched" : "framed"));

// --- FRAMING CONFIGURATION DIALOG ---
// Same dialog-based flow as the artwork and set checkouts: picking a build opens a Teleported
// dialog with the calculator + live preview, instead of expanding inline.
const isFramingDialogOpen = ref(false);
// Collapsed by default — the per-piece breakdown is supplementary detail, and the CTA button
// above it already surfaces the item's total.
const isBreakdownOpen = ref(false);

function closeFramingDialog() {
  nextTick(() => {
    isFramingDialogOpen.value = false;
  });
}

// --- EXISTING FRAME ---
// Pieces of this item that are already framed and have framed photos. "Existing" keeps the item
// exactly as it is; nothing extra is priced or stored beyond the framing label.
const existingFramedPieces = computed(() =>
  pieces.value
    .filter((a: any) => a?.is_work_framed === "Yes")
    .map((a: any) => ({
      id: a.id,
      name: a.name,
      images: String(a.framed_artwork_images ?? "").split(",").map((n) => n.trim()).filter(Boolean),
      cost: a.qam_framing_repair_cost,
    }))
    // a piece only counts when it also has a QAM framing/repair cost
    .filter((a) => a.images.length > 0 && String(a.cost ?? "").trim() !== "")
);
const hasExistingFrameOption = computed(() => canFrame.value && existingFramedPieces.value.length > 0);
const isExistingDialogOpen = ref(false);
// Which framed piece the existing-frame dialog is showing, one at a time
const existingIndex = ref(0);
function goExisting(delta: number) {
  const n = existingFramedPieces.value.length;
  if (n < 2) return;
  existingIndex.value = (existingIndex.value + delta + n) % n;
}
function onExistingKey(e: KeyboardEvent) {
  if (e.key === 'ArrowLeft') { e.preventDefault(); goExisting(-1); }
  else if (e.key === 'ArrowRight') { e.preventDefault(); goExisting(1); }
}
watch(isExistingDialogOpen, (open) => {
  if (open) {
    existingIndex.value = 0;
    window.addEventListener('keydown', onExistingKey);
  } else {
    window.removeEventListener('keydown', onExistingKey);
  }
});
onBeforeUnmount(() => window.removeEventListener('keydown', onExistingKey));
// Cost shown in the existing-frame dialog: the piece's own cost when one piece is framed, the total
// when every framed piece has a numeric cost, otherwise "-" (never a misleading partial total).
const existingFrameCostLabel = computed(() => {
  const list = existingFramedPieces.value;
  if (list.length === 1) return formatFramingCost(list[0].cost);
  const nums = list.map((p) => parseFramingCost(p.cost));
  if (nums.length && nums.every((n) => n !== null)) return formatFramingCost(nums.reduce((a, b) => a + (b as number), 0));
  return "-";
});
const customiseExisting = ref(false);
// True only once the buyer has gone through the customisation and pressed Done. Opening the framing dialog
// and closing it again does NOT count, so the notice asks them to "Complete customisation" instead.
const customisationDone = ref(false);
function onCustomisationDone() {
  customisationDone.value = true;
  closeFramingDialog();
}

function chooseExistingFrame() {
  customiseExisting.value = false;
  customisationDone.value = false;
  choice.value = "Existing";
  specs.value = null;
  nextTick(() => {
    isExistingDialogOpen.value = false;
  });
}

function chooseCustomiseFrame() {
  customiseExisting.value = true;
  customisationDone.value = false;
  // Already on Framed: the calculator is still mounted and won't re-emit, so keep its specs.
  // Clearing them here is what left the preview without a frame.
  if (choice.value !== "Framed") {
    choice.value = "Framed";
    specs.value = null;
  }
  nextTick(() => {
    isExistingDialogOpen.value = false;
    // Go straight to the framing dialog (the mode watcher only fires when the mode changes)
    isFramingDialogOpen.value = true;
  });
}

// Tells the checkout modal one of this item's dialogs is open, so a tap on that dialog is not read as a
// tap "outside" the checkout. The release is delayed on purpose: on touch screens the modal library
// settles a tap outside on the click that FOLLOWS it, so the guard has to outlive that click.
let guardReleaseTimer: ReturnType<typeof setTimeout> | undefined;
watch([isFramingDialogOpen, isExistingDialogOpen], ([framingOpen, existingOpen]) => {
  if (guardReleaseTimer) clearTimeout(guardReleaseTimer);
  if (framingOpen || existingOpen) {
    emit("update:dialogOpen", true);
  } else {
    guardReleaseTimer = setTimeout(() => emit("update:dialogOpen", false), 400);
  }
});
onBeforeUnmount(() => {
  if (guardReleaseTimer) clearTimeout(guardReleaseTimer);
  emit("update:dialogOpen", false);
});

// Auto-open the dialog the moment this item's build is picked (Framed or Stretched). Closing it
// does not clear the choice — the calculator stays mounted and keeps its selections until the
// buyer switches to Unframed.
// Re-opens on EVERY change to a real mode, not just from "no mode" — switching directly from
// Framed to Stretched (or back) goes mode-to-mode with no null in between, and the buyer still
// needs the dialog for whichever one they just picked.
watch(mode, (m) => {
  if (m) {
    // Deferred by a tick for the same reason the artwork/set checkouts defer it: so the checkbox's
    // own click event fully propagates before the Teleport backdrop renders.
    nextTick(() => {
      isFramingDialogOpen.value = true;
    });
  } else {
    nextTick(() => {
      isFramingDialogOpen.value = false;
    });
  }
});

// Picking a build that prices nothing, or dropping the choice entirely, must not leave stale specs
// behind — they would be stored on the order and charged for.
const setChoice = (value: string, checked: boolean) => {
  choice.value = checked ? value : "";
  if (!mode.value) specs.value = null;
};

// Every piece priced at the one specification, each at its own size. Excluded pieces ride along at
// ₹0 with the reason, so the buyer can see why a piece is not being charged for.
const framingByArtwork = computed(() => {
  if (!specs.value || !mode.value) return [];

  return pieces.value.map((art: any) => {
    const { widthInches, heightInches } = parseArtworkDimensions(art);
    const excluded = exclusionReason(art);
    const base = {
      artwork: art,
      sizeLabel: art?.display_size || art?.size_in_inches || "",
      artworkType: null as string | null,
      breakdown: null as ReturnType<typeof calculateFramingBreakdown> | null,
    };

    if (excluded) return { ...base, cost: 0, note: excluded, priced: false };
    if (!widthInches || !heightInches) {
      return { ...base, cost: 0, note: "Size unavailable", priced: false };
    }

    const artworkType = resolveFramingArtworkType(art?.artwork_category);
    const breakdown = calculateFramingBreakdown({
      artworkType,
      lengthIn: widthInches,
      heightIn: heightInches,
      specs: specsForArtworkType(specs.value as any, artworkType),
      mode: mode.value as FramingMode,
    });

    // Past the pricing sheet's largest rung the quote can only be partial, so the piece is not
    // charged online at all — a partial number here would be a wrong number on the invoice.
    if (breakdown.outOfRange) {
      return {
        ...base,
        artworkType,
        breakdown,
        cost: 0,
        priced: false,
        note: "Size beyond online pricing — we will confirm this piece",
      };
    }

    return { ...base, artworkType, breakdown, cost: breakdown.total, priced: true, note: null as string | null };
  });
});

const framingTotalCost = computed(() =>
  framingByArtwork.value.reduce((sum, item) => sum + (item.cost || 0), 0)
);

const pricedCount = computed(() => framingByArtwork.value.filter((i) => i.priced).length);

const hasUnpriced = computed(() => framingByArtwork.value.some((i) => !i.priced));

const showFramePreview = computed(() => Boolean(mode.value && primaryPiece.value && specs.value));

// What actually gets stored on the order for this item — built from the same raw calculator output
// (`specs`) that already drives the live preview and the on-screen breakdown above, reshaped by the
// shared builder every checkout uses. Doesn't touch `specs`/`framingByArtwork` themselves.
const framingRecord = computed<PieceFraming | SetFramingRecord | null>(() => {
  if (!requiresChoice.value) return null;

  if (isSet.value) {
    return buildSetFramingRecord({
      setId: props.item?.id,
      choice: choice.value,
      configuredOn: primaryPiece.value
        ? {
            artworkId: primaryPiece.value.id,
            artworkName: primaryPiece.value.name,
            sizeLabel: primaryPiece.value.display_size || primaryPiece.value.size_in_inches || "",
          }
        : null,
      rawSpecs: specs.value,
      configuredOnWidthIn: primaryDimensions.value.widthInches,
      configuredOnHeightIn: primaryDimensions.value.heightInches,
      pieces: framingByArtwork.value.map((item) => ({
        artworkId: item.artwork.id,
        artworkName: item.artwork.name,
        sizeLabel: item.sizeLabel,
        widthIn: parseArtworkDimensions(item.artwork).widthInches,
        heightIn: parseArtworkDimensions(item.artwork).heightInches,
        artworkCategory: resolveFramingArtworkType(item.artwork?.artwork_category),
        cost: item.cost || 0,
        reason: item.priced ? null : noteToFramingReason(item.note),
      })),
    });
  }

  return buildPieceFraming({
    artworkId: props.item?.id,
    artworkName: props.item?.name,
    sizeLabel: props.item?.display_size || props.item?.size || "",
    widthIn: primaryDimensions.value.widthInches,
    heightIn: primaryDimensions.value.heightInches,
    artworkCategory: resolveFramingArtworkType(props.item?.artwork_category),
    isAlreadyFramed: props.item?.is_work_framed === "Yes",
    choice: choice.value,
    rawSpecs: specs.value,
  });
});

const framePreviewProps = computed(() => ({
  specifications: specs.value,
  artworkLength: primaryDimensions.value.widthInches,
  artworkHeight: primaryDimensions.value.heightInches,
  artworkImageSrc: getArtworkImageSrc(primaryPiece.value?.photo_name ?? "", 720),
  photoWidth: primaryPiece.value?.photo_width ?? undefined,
  photoHeight: primaryPiece.value?.photo_height ?? undefined,
  mode: mode.value ?? ("frame" as FramingMode),
  unit: sizeUnit.value,
}));

// From lg up the checkout parks every preview together in its own column, so this item hands its
// preview over rather than drawing it. Below lg the modal is a single column and the preview stays
// here, with the controls it reflects. Matches the lg: prefix on the checkout's own layout.
const isPreviewBesideForm = useMediaQuery("(min-width: 1024px)");

// A set's preview is drawn on one piece, so the label says which — otherwise a column of previews
// gives no clue which item each one belongs to.
const previewLabel = computed(() => {
  const itemName = props.item?.name || "Untitled";
  if (!isSet.value) return itemName;
  return `${itemName} · ${primaryPiece.value?.name || "first piece"}`;
});

// Shipping quotes the box, so it needs the depth the buyer actually chose — the frame's for a
// framed piece, the stretcher bars' for a stretched one.
const frameDepthInches = computed(() => {
  if (!mode.value) return 0;
  if (mode.value === "stretch") return stretcherBarDepthInches(specs.value?.stretcherBar);
  return Number(specs.value?.frameDepth) || 2;
});

// An answer counts as given once a choice is made and, where that choice is a build, the
// calculator has emitted the specifications it is priced from — and for Paper + Framed, at least
// one of Mount / Side Depth has actually been turned on (see needsMountOrSideDepth).
const isComplete = computed(() => {
  if (!requiresChoice.value) return true;
  if (!choice.value) return false;
  if (!mode.value) return true;
  return Boolean(specs.value) && !needsMountOrSideDepth(specs.value);
});

const selection = computed<CartFramingSelection>(() => ({
  cartItemId: props.item?.cartItemId,
  itemType: isSet.value ? "set" : "artwork",
  itemName: props.item?.name || "",
  choice: choice.value,
  mode: mode.value,
  specs: specs.value,
  // "Existing": the already-framed pieces keep their frames, charged at their QAM framing/repair cost
  cost: choice.value === "Existing"
    ? (totalFramingCost(existingFramedPieces.value.map((p) => p.cost)) ?? 0)
    : framingTotalCost.value,
  preview: showFramePreview.value
    ? { label: previewLabel.value, props: framePreviewProps.value }
    : null,
  framingRecord: framingRecord.value,
  frameDepthInches: frameDepthInches.value,
  framedArtworkIds: mode.value ? eligiblePieces.value.map((a: any) => a.id) : [],
  perArtwork: framingByArtwork.value.map((item) => ({
    id: item.artwork?.id,
    name: item.artwork?.name,
    size: item.sizeLabel,
    artworkType: item.artworkType,
    cost: item.cost,
    note: item.note,
    components: item.breakdown?.components,
  })),
  requiresChoice: requiresChoice.value,
  isComplete: isComplete.value,
}));

// The computed rebuilds on every change, so a plain watch is enough to keep the parent in step.
// Post flush keeps the first report out of the parent's own render pass.
watch(selection, (value) => emit("update:selection", value), { immediate: true, flush: "post" });
</script>

<template>
  <div class="border border-gray-200 rounded-[8px] p-4 flex flex-col gap-4">
    <!-- Which item is being answered for -->
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-semibold text-gray-900 truncate">{{ item.name || 'Untitled' }}</span>
        <span class="text-[12px] text-gray-500 mt-0.5">
          <template v-if="isSet">Set · {{ pieces.length }} {{ pieces.length === 1 ? 'piece' : 'pieces' }}</template>
          <template v-else>{{ item.display_size || item.size || '—' }}</template>
        </span>
      </div>
      <span v-if="item.artist" class="text-[11px] text-gray-500 uppercase tracking-wider shrink-0">{{ item.artist }}</span>
    </div>

    <!-- Nothing to ask: no piece here can take a frame -->
    <p v-if="!requiresChoice" class="text-[12px] text-gray-500">
      <template v-if="isSet">This artwork cannot be framed. Do reach out on <a href="mailto:connect@quietart.com" class="underline underline-offset-2">connect@quietart.com</a> if you have any questions.</template>
      <template v-else>This artwork is not framable, so it ships as it is.</template>
    </p>

    <template v-else>
      <!-- Same Unframed/Framed/Stretched choice regardless of is_work_framed — an already-framed
           artwork used to get a separate "keep this frame / choose a new frame" question here, but
           it is asked like every other piece now. -->
      <!-- Already-framed pieces with photos: communicate the current frame instead of offering it as an option -->
      <div v-if="hasExistingFrameOption" class="flex flex-col gap-3 bg-gray-50 border border-gray-200 rounded-[8px] px-4 py-3">
        <p class="text-sm text-gray-800 leading-relaxed">
          <template v-if="!isSet">This artwork is currently framed.</template>
          <template v-else>{{ existingFramedPieces.length > 1 ? 'Some artworks in this set are currently framed.' : 'An artwork in this set is currently framed.' }}</template>
          <template v-if="!isSet">Take a look at the existing frame, or explore customisation options to make it your own.</template>
          <template v-else>Take a look at the existing {{ existingFramedPieces.length > 1 ? 'frames' : 'frame' }}, or explore customisation options to make {{ existingFramedPieces.length > 1 ? 'them' : 'it' }} your own.</template>
          Customisation may add to the cost and delivery time.
        </p>
        <div class="flex gap-3">
          <!-- After a choice is made the two buttons become one, doubling as the success message. Tapping it
               reopens the existing-frame dialog, so the buyer can still change their mind. -->
          <!-- "Customised frame selected" only after the customisation was really finished (Done). If the buyer
               opened the customisation and closed it again, the button says "Complete customisation" and takes them back. -->
          <Button
            v-if="choice === 'Existing' || customiseExisting"
            size="sm"
            :variant="customiseExisting && !customisationDone ? 'primary' : 'secondary'"
            class="flex-1 justify-center"
            @click.prevent="customiseExisting && !customisationDone ? (isFramingDialogOpen = true) : (isExistingDialogOpen = true)"
          >
            <Icon v-if="!customiseExisting || customisationDone" name="lucide:check" class="w-4 h-4 text-green-600" />
            {{ customiseExisting ? (customisationDone ? 'Customised frame selected' : 'Complete customisation') : 'Current frame selected' }}
          </Button>
          <template v-else>
            <Button size="sm" class="flex-1 justify-center" variant="primary" @click.prevent="isExistingDialogOpen = true">See current {{ existingFramedPieces.length > 1 ? 'frames' : 'frame' }}</Button>
            <Button size="sm" class="flex-1 justify-center" variant="secondary" @click.prevent="chooseCustomiseFrame">I'll customise</Button>
          </template>
        </div>
      </div>
      <div v-if="!hasExistingFrameOption" class="flex gap-4">
        <label class="flex items-center gap-2 cursor-pointer" @click.stop>
          <input type="checkbox" :checked="choice === 'Unframed'" @change.stop="setChoice('Unframed', ($event.target as HTMLInputElement).checked)" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
          <span class="text-sm text-gray-700">Unframed</span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer" @click.stop>
          <input type="checkbox" :checked="choice === 'Framed'" @change.stop="setChoice('Framed', ($event.target as HTMLInputElement).checked)" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
          <span class="text-sm text-gray-700">Framed</span>
        </label>
        <!-- Canvas only: mounted on stretcher bars and hung without a frame -->
        <label v-if="canStretch" class="flex items-center gap-2 cursor-pointer" @click.stop>
          <input type="checkbox" :checked="choice === 'Stretched'" @change.stop="setChoice('Stretched', ($event.target as HTMLInputElement).checked)" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
          <span class="text-sm text-gray-700">Stretched</span>
        </label>
      </div>

      <!-- When a build is active, show a compact CTA that opens this item's framing dialog -->
      <div v-if="mode && primaryPiece" class="flex flex-col gap-3">
        <button
          type="button"
          @click.stop.prevent="isFramingDialogOpen = true"
          class="w-full flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 hover:border-gray-400 hover:bg-white transition-all duration-200 rounded-[8px] px-4 py-3 cursor-pointer group"
        >
          <div class="flex items-center gap-2.5">
            <Icon name="lucide:panel-left" class="w-4 h-4 text-gray-500 shrink-0 group-hover:text-gray-800 transition-colors" />
            <div class="flex flex-col items-start text-left">
              <span class="text-[13px] font-semibold text-gray-900 leading-tight">
                {{ specs ? `${framingNoun} configured` : `Configure ${framingNoun.toLowerCase()} options` }}
              </span>
              <span v-if="isSet" class="text-[11px] text-gray-500">Applies to every piece in the set</span>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span v-if="framingTotalCost > 0" class="text-sm font-bold text-gray-900">
              + ₹{{ Math.round(framingTotalCost).toLocaleString('en-IN') }}
            </span>
            <span v-else-if="specs" class="text-sm font-bold text-gray-900">-</span>
            <Icon name="lucide:arrow-right" class="w-4 h-4 text-gray-400 group-hover:text-gray-700 group-hover:translate-x-0.5 transition-all" />
          </div>
        </button>
        <!-- Full width, with room to actually explain — the inline "-" above just flags that
             there's no number; this is where the buyer learns why. -->
        <p v-if="specs && framingTotalCost <= 0" class="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 leading-relaxed">
          ⚠ {{ framingNoun }} at this size is confirmed personally by our team, so it is
          <strong>not included in the amount payable now</strong>. Your selections are saved with the order and we
          will share the cost with you within 48 hours.
        </p>

        <!-- Per-piece split of the same specification — collapsed by default so the item stays
             compact; the CTA button above already surfaces the total. A single artwork is its own
             total already, so this is set-only. -->
        <div v-if="isSet && framingByArtwork.length" class="bg-[#f8f9fa] border border-gray-100 rounded-[8px] p-4 flex flex-col gap-3">
          <div
            class="flex items-center justify-between gap-3 cursor-pointer select-none"
            @click="isBreakdownOpen = !isBreakdownOpen"
          >
            <h5 class="font-bold text-xs uppercase tracking-widest">{{ framingNoun }} across the set</h5>
            <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200" :class="{ 'rotate-180': isBreakdownOpen }" />
          </div>
          <div v-show="isBreakdownOpen" class="flex flex-col gap-3">
            <div class="flex flex-col divide-y divide-gray-100 border-t border-gray-100 pt-3">
              <div
                v-for="row in framingByArtwork"
                :key="row.artwork.id"
                class="flex items-start justify-between gap-4 py-2 first:pt-0"
              >
                <div class="flex flex-col min-w-0">
                  <div class="text-[13px] font-semibold text-gray-900 truncate">{{ row.artwork.name || 'Untitled piece' }}</div>
                  <div class="text-[13px] text-gray-500 mt-0.5">
                    {{ row.sizeLabel || '—' }}<template v-if="row.note"> · {{ row.note }}</template>
                  </div>
                </div>
                <span class="text-[14px] font-medium text-gray-700 shrink-0">
                  {{ row.priced ? `₹${Intl.NumberFormat('en-IN').format(Math.round(row.cost))}` : '—' }}
                </span>
              </div>
            </div>
            <div class="bg-white border border-gray-200 rounded-[8px] flex items-center justify-between p-4">
              <div class="text-[13px] font-bold text-[#1a1a2e] uppercase tracking-widest">{{ framingNoun }} Total</div>
              <div class="text-[16px] font-bold text-[#1a1a2e]">₹{{ Intl.NumberFormat("en-IN").format(Math.round(framingTotalCost)) }}</div>
            </div>
            <p v-if="hasUnpriced" class="text-[12px] text-gray-500">
              Pieces marked “—” are not charged for {{ framingNoun.toLowerCase() }} here. Our team will confirm them with you within 48 hours.
            </p>
          </div>
        </div>
      </div>
    </template>

    <span v-if="error" class="text-red-500 text-xs">{{ error }}</span>
  </div>

  <!-- FRAMING CONFIGURATION DIALOG -->
  <!-- Teleported to <body> so it sits above the cart checkout modal in the stacking context.
       v-if="mode && primaryPiece" keeps the calculator mounted as long as this item has a build
       selected — preserving its selections across open/close cycles. CSS visibility (not v-if)
       controls whether the overlay is actually shown. -->
  <Teleport to="body">
    <div
      v-if="mode && primaryPiece"
      class="framing-overlay"
      :class="isFramingDialogOpen ? 'framing-overlay--visible' : 'framing-overlay--hidden'"
      aria-modal="true"
      role="dialog"
      aria-label="Configure framing"
    >
      <!-- Backdrop -->
      <Transition name="framing-fade">
        <div
          v-show="isFramingDialogOpen"
          class="framing-backdrop"
          @click.prevent="closeFramingDialog"
        />
      </Transition>

      <!-- Panel -->
      <Transition name="framing-slide">
        <div v-show="isFramingDialogOpen" class="framing-panel">

          <!-- Header -->
          <div class="framing-panel__header">
            <div>
              <h2 class="framing-panel__title">
                {{ mode === 'stretch' ? 'Stretching Options' : 'Framing Options' }}
              </h2>
              <p class="framing-panel__subtitle">
                {{ item.name || 'Untitled' }}<template v-if="isSet"> — configured once, applied to every piece in the set</template>
              </p>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <MeasurementUnitToggle v-model="sizeUnit" placement="left" />
              <button
                type="button"
                @click.prevent="closeFramingDialog"
                class="framing-panel__close"
                aria-label="Close framing dialog"
              >
                <Icon name="lucide:x" class="w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- Body: preview + calculator side by side -->
          <div class="framing-panel__body">

            <!-- LEFT: Live Frame Preview -->
            <div class="framing-panel__preview">
              <div class="framing-panel__preview-inner">
                <FrameVisualizer v-bind="framePreviewProps" />
              </div>
            </div>

            <!-- RIGHT: Framing Calculator -->
            <div class="framing-panel__calculator">
              <p class="framing-panel__section-label">
                {{ mode === 'stretch' ? 'Stretching' : 'Framing' }} Specifications
              </p>
              <FramingCalculator
                :artworkCategory="primaryPiece.artwork_category || 'Canvas'"
                :initialLength="primaryDimensions.widthInches"
                :initialHeight="primaryDimensions.heightInches"
                :sizeLabel="primaryPiece.display_size || primaryPiece.size_in_inches || ''"
                :mode="mode"
                :step-by-step="true"
                :totalLabel="isSet ? 'This piece' : undefined"
                :unit="sizeUnit"
                @update:specifications="specs = $event"
                @done="onCustomisationDone"
              />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>

  <!-- EXISTING FRAME DIALOG: shows the framed photos of this item's already-framed pieces -->
  <Teleport to="body">
    <div v-if="isExistingDialogOpen" class="framing-overlay framing-overlay--visible" aria-modal="true" role="dialog" aria-label="Existing frame">
      <div class="framing-backdrop" @click.prevent="isExistingDialogOpen = false" />
      <div class="framing-panel" style="max-width: 32rem; height: auto; max-height: 90dvh;">
        <div class="framing-panel__header">
          <h2 class="framing-panel__title">Take a look at the existing {{ existingFramedPieces.length > 1 ? 'frames' : 'frame' }}</h2>
          <button type="button" class="framing-panel__close" aria-label="Close" @click.prevent="isExistingDialogOpen = false">
            <Icon name="lucide:x" class="w-4 h-4" />
          </button>
        </div>
        <!-- Own wrapper (not .framing-panel__body, which turns into a row on desktop) -->
        <div class="flex flex-col gap-4 p-5 overflow-y-auto min-h-0">
          <!-- One framed piece at a time. With several, the buyer steps through them with the arrow buttons
               or the left / right arrow keys (see existingIndex / goExisting). -->
          <div class="relative">
            <template v-for="(piece, idx) in existingFramedPieces" :key="piece.id">
              <div v-show="idx === existingIndex" class="flex flex-col gap-3 items-center">
                <p v-if="existingFramedPieces.length > 1" class="text-sm font-semibold text-gray-900 self-start w-full flex justify-between gap-3"><span>{{ piece.name }}</span><span class="font-normal text-gray-600">{{ formatFramingCost(piece.cost) }}</span></p>
                <img
                  v-for="name in piece.images"
                  :key="name"
                  :src="getFramedArtworkImageSrc(name)"
                  :alt="`${piece.name} framed`"
                  class="max-w-full max-h-[45vh] w-auto h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </template>
            <template v-if="existingFramedPieces.length > 1">
              <button type="button" aria-label="Previous frame" class="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-white/95 border border-gray-200 shadow-md text-gray-800 hover:bg-white cursor-pointer" @click.prevent="goExisting(-1)">
                <Icon name="lucide:chevron-left" class="w-5 h-5" />
              </button>
              <button type="button" aria-label="Next frame" class="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-white/95 border border-gray-200 shadow-md text-gray-800 hover:bg-white cursor-pointer" @click.prevent="goExisting(1)">
                <Icon name="lucide:chevron-right" class="w-5 h-5" />
              </button>
              <p class="text-center text-xs text-gray-500 mt-3">{{ existingIndex + 1 }} / {{ existingFramedPieces.length }}</p>
            </template>
          </div>
          <div class="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-[8px] px-4 py-3">
            <span class="text-sm font-semibold text-gray-900">Cost</span>
            <span class="text-xl font-bold text-gray-900 leading-none">{{ existingFrameCostLabel }}</span>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <Button class="flex-1 justify-center" variant="primary" @click.prevent="chooseExistingFrame">I'll go with this framing</Button>
            <Button class="flex-1 justify-center" variant="secondary" @click.prevent="chooseCustomiseFrame">I'll customise</Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* ─── Framing overlay wrapper ──────────────────────────────────────────────── */
.framing-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.framing-overlay--visible { pointer-events: auto; }
.framing-overlay--hidden  { pointer-events: none; }

/* ─── Backdrop ─────────────────────────────────────────────────────────────── */
.framing-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* ─── Panel ────────────────────────────────────────────────────────────────── */
.framing-panel {
  position: relative;
  z-index: 10;
  background: var(--color-primary, #fff);
  width: 100%;
  max-width: 72rem;
  height: clamp(550px, 82dvh, 750px);
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.5rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.25),
    0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
@media (max-width: 1023px) {
  .framing-panel {
    height: 88dvh;
    max-height: 88dvh;
    border-radius: 0.5rem;
  }
}

/* ─── Header ───────────────────────────────────────────────────────────────── */
.framing-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
@media (max-width: 1023px) {
  .framing-panel__header {
    padding: 0.875rem 1rem;
  }
}
.framing-panel__title {
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-on-primary-heading, #111);
  margin: 0;
}
.framing-panel__subtitle {
  font-size: 0.8125rem;
  color: var(--color-on-primary-body, #555);
  margin: 0.25rem 0 0;
  line-height: 1.5;
}
@media (max-width: 639px) {
  .framing-panel__subtitle {
    font-size: 0.75rem;
    line-height: 1.35;
  }
}

.framing-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  color: var(--color-on-primary-body, #6b7280);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
  padding: 0;
}
.framing-panel__close:hover {
  background: var(--ui-bg-elevated, rgba(0, 0, 0, 0.06));
  color: var(--color-on-primary-heading, #111);
}

/* ─── Body ─────────────────────────────────────────────────────────────────── */
.framing-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
@media (min-width: 1024px) {
  .framing-panel__body {
    flex-direction: row;
    overflow-y: hidden;
  }
}

.framing-panel__section-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-on-primary-body, #888);
  margin: 0 0 0.75rem;
  flex-shrink: 0;
}
@media (max-width: 1023px) {
  .framing-panel__section-label {
    margin-bottom: 0.375rem;
  }
}

.framing-panel__preview {
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.5rem;
  /* Without this, a flex item's automatic minimum width is its CONTENT's natural size — and the
     frame inside renders at an explicit pixel width (outerWpx) that doesn't shrink on its own.
     That let this column (and the whole dialog with it) grow past its intended 45% whenever a
     bigger front-width/mount made that pixel width bigger, instead of the frame scaling down to
     fit. min-width: 0 is what actually makes "width: 45%" binding rather than a mere suggestion. */
  min-width: 0;
}
@media (max-width: 1023px) {
  .framing-panel__preview {
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
    overflow: visible;
  }
}
@media (min-width: 1024px) {
  .framing-panel__preview {
    width: 45%;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    overflow-y: auto;
  }
}
/* Same reasoning as .framing-panel__preview's min-width: 0 above, one level deeper — without it,
   this div's own automatic minimum width is still FrameVisualizer's natural content size, which
   defeats the parent's min-width: 0 by just moving the leak down one level. overflow: hidden backs
   it up: a flex/block box with non-visible overflow gets an automatic minimum size of 0 by spec,
   so even a future layout change that drops the explicit min-width can't reopen this. */
.framing-panel__preview-inner {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.framing-panel__calculator {
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.5rem;
  min-width: 0;
}
@media (max-width: 1023px) {
  .framing-panel__calculator {
    padding: 1rem;
    flex-shrink: 0;
    overflow: visible;
  }
}
@media (min-width: 1024px) {
  .framing-panel__calculator {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
}

/* ─── Transitions ──────────────────────────────────────────────────────────── */
.framing-fade-enter-active,
.framing-fade-leave-active { transition: opacity 0.25s ease; }
.framing-fade-enter-from,
.framing-fade-leave-to { opacity: 0; }

.framing-slide-enter-active {
  transition: opacity 0.25s ease, transform 0.28s ease-out;
}
.framing-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.framing-slide-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.98);
}
.framing-slide-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.99);
}
</style>
